import React from 'react';

const HERO_BG = 'https://img.freepik.com/free-photo/portrait-engineers-work-hours-job-site_23-2151589536.jpg?ga=GA1.1.341967142.1748248136&semt=ais_items_boosted&w=740';

const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-slate-900">
      {/* Background with enhanced overlay */}
      <div className="absolute inset-0">
        <img 
          src={HERO_BG} 
          alt="Construction professionals at work" 
          className="w-full h-full object-cover object-center scale-105 transition-transform duration-700 hover:scale-100" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/92 via-blue-950/88 to-slate-800/92" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/20 via-transparent to-blue-900/20" />
      </div>

      {/* Enhanced animated grid pattern overlay */}
      <div className="absolute inset-0 opacity-8">
        <div className="w-full h-full bg-[linear-gradient(rgba(59,130,246,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.2)_1px,transparent_1px)] bg-[size:60px_60px] animate-pulse" />
      </div>

      {/* Enhanced floating geometric shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-r from-blue-500/15 to-cyan-400/15 rounded-full blur-xl animate-float" />
        <div className="absolute top-3/4 right-1/4 w-48 h-48 bg-gradient-to-r from-cyan-400/12 to-purple-500/12 rounded-full blur-2xl animate-float-delayed" />
        <div className="absolute bottom-1/3 left-1/3 w-24 h-24 bg-gradient-to-r from-blue-400/18 to-cyan-300/18 rounded-full blur-lg animate-float-slow" />
        <div className="absolute top-1/2 right-1/3 w-16 h-16 bg-gradient-to-r from-purple-400/15 to-blue-500/15 rounded-full blur-md animate-float-reverse" />
      </div>

      {/* Main content with proper mobile spacing */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-12 sm:py-16 lg:py-20">
        <div className="text-center space-y-6 sm:space-y-8 lg:space-y-10">
          {/* Enhanced company badge */}
          <div className="inline-flex items-center gap-2 bg-white/12 backdrop-blur-md border border-white/25 rounded-full px-4 py-2 sm:px-5 sm:py-2.5 text-xs sm:text-sm font-medium text-white/95 animate-fade-in shadow-lg">
            <div className="w-2 h-2 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full animate-pulse shadow-lg shadow-cyan-400/50" />
            <span className="hidden sm:inline">Leading Construction Excellence Since 2010</span>
            <span className="sm:hidden">Excellence Since 2010</span>
          </div>

          {/* Enhanced main heading with better mobile typography */}
          <div className="space-y-3 sm:space-y-4 animate-slide-up">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[1.1] sm:leading-tight tracking-tight">
              <span className="block mb-2 sm:mb-0">Engineering your</span>
              <span className="block bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent filter drop-shadow-sm">
                Dreams
              </span>
              <span className="block mt-1 sm:mt-0">With Us</span>
            </h1>
          </div>

          {/* Enhanced subtitle with better mobile spacing */}
          <div className="max-w-2xl lg:max-w-3xl mx-auto animate-slide-up-delayed px-2 sm:px-0">
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-slate-200 leading-relaxed font-light">
              Transforming Kenya's landscape with cutting-edge engineering solutions, 
              sustainable construction practices, and unwavering commitment to excellence.
            </p>
          </div>

          {/* Enhanced stats row with better mobile layout */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-8 lg:gap-12 py-6 sm:py-8 animate-fade-in-delayed">
            <div className="text-center group">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-cyan-300 bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">200+</div>
              <div className="text-xs sm:text-sm text-slate-400 uppercase tracking-wider font-medium">Projects Completed</div>
            </div>
            <div className="w-12 h-px sm:w-px sm:h-12 bg-gradient-to-r sm:bg-gradient-to-b from-transparent via-slate-500 to-transparent" />
            <div className="text-center group">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">15+</div>
              <div className="text-xs sm:text-sm text-slate-400 uppercase tracking-wider font-medium">Years Experience</div>
            </div>
            <div className="w-12 h-px sm:w-px sm:h-12 bg-gradient-to-r sm:bg-gradient-to-b from-transparent via-slate-500 to-transparent" />
            <div className="text-center group">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-400 to-purple-300 bg-clip-text text-transparent mb-1 group-hover:scale-110 transition-transform duration-300">100%</div>
              <div className="text-xs sm:text-sm text-slate-400 uppercase tracking-wider font-medium">Client Satisfaction</div>
            </div>
          </div>

          {/* Enhanced CTA buttons with proper mobile spacing */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-4 sm:pt-6 pb-8 sm:pb-12 animate-slide-up-delayed">
            <a href="#services" className="w-full sm:w-auto">
              <button className="group relative w-full sm:w-auto px-6 sm:px-8 lg:px-10 py-3 sm:py-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-blue-700 text-white font-semibold rounded-xl sm:rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30 hover:-translate-y-1 active:translate-y-0 border border-cyan-400/20">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="relative flex items-center justify-center gap-2 text-sm sm:text-base font-semibold">
                  Explore Our Services
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </button>
            </a>

            <a href="#portfolio" className="w-full sm:w-auto">
              <button className="group relative w-full sm:w-auto px-6 sm:px-8 lg:px-10 py-3 sm:py-4 bg-white/12 backdrop-blur-md border border-white/25 text-white font-semibold rounded-xl sm:rounded-2xl transition-all duration-300 hover:bg-white/20 hover:border-white/35 hover:-translate-y-1 active:translate-y-0 shadow-lg hover:shadow-xl">
                <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl sm:rounded-2xl" />
                <span className="relative flex items-center justify-center gap-2 text-sm sm:text-base font-semibold">
                  View Our Portfolio
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </span>
              </button>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce-slow">
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-scroll-indicator"></div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1); 
          }
          33% { 
            transform: translateY(-15px) rotate(5deg) scale(1.05); 
          }
          66% { 
            transform: translateY(-25px) rotate(-3deg) scale(0.95); 
          }
        }
        @keyframes float-delayed {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg) scale(1); 
          }
          33% { 
            transform: translateY(-20px) rotate(-8deg) scale(1.02); 
          }
          66% { 
            transform: translateY(-35px) rotate(12deg) scale(0.98); 
          }
        }
        @keyframes float-slow {
          0%, 100% { 
            transform: translateY(0px) scale(1); 
          }
          50% { 
            transform: translateY(-18px) scale(1.1); 
          }
        }
        @keyframes float-reverse {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          50% { 
            transform: translateY(-12px) rotate(8deg); 
          }
        }
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes fade-in {
          from { 
            opacity: 0; 
            transform: translateY(20px);
          }
          to { 
            opacity: 1; 
            transform: translateY(0);
          }
        }
        @keyframes bounce-slow {
          0%, 100% { 
            transform: translateX(-50%) translateY(0); 
          }
          50% { 
            transform: translateX(-50%) translateY(-12px); 
          }
        }
        @keyframes scroll-indicator {
          0% { 
            transform: translateY(0); 
            opacity: 1; 
          }
          100% { 
            transform: translateY(24px); 
            opacity: 0; 
          }
        }
        
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 10s ease-in-out infinite 2s;
        }
        .animate-float-slow {
          animation: float-slow 12s ease-in-out infinite 4s;
        }
        .animate-float-reverse {
          animation: float-reverse 7s ease-in-out infinite 1s;
        }
        .animate-slide-up {
          animation: slide-up 1s ease-out;
        }
        .animate-slide-up-delayed {
          animation: slide-up 1s ease-out 0.4s both;
        }
        .animate-fade-in {
          animation: fade-in 1s ease-out 0.7s both;
        }
        .animate-fade-in-delayed {
          animation: fade-in 1s ease-out 1s both;
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite 2s;
        }
        .animate-scroll-indicator {
          animation: scroll-indicator 2s ease-in-out infinite;
        }

        /* Custom responsive utilities */
        @media (max-width: 640px) {
          .animate-slide-up {
            animation-duration: 0.8s;
          }
          .animate-slide-up-delayed {
            animation-duration: 0.8s;
            animation-delay: 0.3s;
          }
        }
      `}</style>
    </section>
  )
}

export default Hero;